import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import { useCallback, useContext, useState } from "react";
import {
  AccessKind,
  Role,
  TaskKind,
  Tool,
  User,
} from "../../../app/backendTypes";
import { HttpClientContext, X_REQUEST_ID } from "../../../app/client";
import { useAppSelector } from "../../../app/hooks";
import UserSpan from "../users/UserSpan";
import Morselbar from "../Morselbar";

function BoardingDialog(props: {
  onboarded: boolean;
  open: boolean;
  setOpen: (val: boolean) => void;
  callback: () => void;
  tool: Tool;
  user: User;
}) {
  const { onboarded, open, setOpen, callback, tool, user } = props;

  const boardingAction = onboarded ? "offboarded" : "onboarded";
  const boardingPreposition = onboarded ? "from" : "in";
  const boardingColor = onboarded ? "error" : "success.main";
  const confirmColor = onboarded ? "error" : "success";

  const isAdmin = useAppSelector((state) => state.auth.roles).includes(
    Role.Administrator
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [createNote, setCreateNote] = useState<string>("");
  const [override, setOverride] = useState(false);

  const client = useContext(HttpClientContext);

  const createBoardingTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    const note = createNote || undefined;

    const body: TaskKind = {
      kind: "ToolAccess",
      inner: {
        kind: onboarded ? AccessKind.Off : AccessKind.On,
        tool: tool,
        target: user.employee.id,
        note,
      },
    };

    client
      ?.post(`/api/tasks?override_approvals=${override}`, body)
      .then(() => callback())
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    createNote,
    onboarded,
    tool,
    user.employee.id,
    client,
    override,
    callback,
  ]);

  return (
    <>
      <Dialog open={open}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography>
              <UserSpan user={user} variant="body1" /> will be{" "}
              <Typography component="span" color={boardingColor}>
                {boardingAction}
              </Typography>{" "}
              {boardingPreposition}{" "}
              <Typography component="span" color="secondary">
                {tool}
              </Typography>
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <br />
          <TextField
            onChange={(e) => setCreateNote(e.target.value.trim())}
            placeholder="(Optional) Add a note"
            label="Note"
            size="small"
            fullWidth
          />

          <br />
          <br />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              {isAdmin && !onboarded && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={override}
                      onChange={(e) => setOverride(e.target.checked)}
                    />
                  }
                  label="Skip approvals"
                />
              )}
            </Box>

            <Button
              onClick={() => createBoardingTask()}
              disabled={loading}
              color={confirmColor}
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to create task"
        error={error}
      />
    </>
  );
}

export default BoardingDialog;
